import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import { ContentCopy, Save, History } from '@mui/icons-material';
import { JsonEditor } from '@shared/components/JsonEditor/JsonEditor';
import type { PlaybookSection } from '../types';
import type { MappingVersion } from '@modules/offers/services/api';
import useFieldMapping from '../hooks/useFieldMapping';
import { enqueueSnackbar } from 'notistack';
import { VersionHistoryDialog } from './VersionHistoryDialog';
import { usePermission } from '@modules/auth/hooks';
import { Module } from '@modules/admin/types';

interface FieldMappingTabProps {
  section: PlaybookSection;
}

export default function FieldMappingTab({ section }: FieldMappingTabProps) {
  const {
    data,
    isLoading,
    error,
    mappingJson,
    setMappingJson,
    webhookUrl,
    setWebhookUrl,
    prodWebhookUrl,
    setProsWebhookUrl,
    saveMapping,
    saving,
  } = useFieldMapping(section);
  const fields = data?.data.fields || [];
  const versions: MappingVersion[] = data?.data.versions || [];
  const currentVersion = data?.data.currentVersion || '';
  const { canAdd, canUpdate } = usePermission(Module.PLAYBOOK);

  const [versionDialogOpen, setVersionDialogOpen] = useState(false);

  // Store original data to detect changes
  const [originalData, setOriginalData] = useState<{
    mappingJson: Record<string, any>;
    webhookUrl: string;
    prodWebhookUrl: string;
  }>({ mappingJson: {}, webhookUrl: '', prodWebhookUrl: '' });

  // Update original data when API data loads
  React.useEffect(() => {
    if (data?.data) {
      setOriginalData({
        mappingJson: data.data.response || {},
        webhookUrl: data.data.webhookUrl || '',
        prodWebhookUrl: data.data.prodWebhookUrl || '',
      });
    }
  }, [data]);

  // Check if there are any changes
  const hasChanges = React.useMemo(() => {
    const mappingChanged = JSON.stringify(mappingJson) !== JSON.stringify(originalData.mappingJson);
    const webhookChanged = webhookUrl !== originalData.webhookUrl;
    const prodWebhookChanged = prodWebhookUrl !== originalData.prodWebhookUrl;
    return mappingChanged || webhookChanged || prodWebhookChanged;
  }, [mappingJson, webhookUrl, prodWebhookUrl, originalData]);

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(`Copied '${text}'`, {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    } catch (e) {
      enqueueSnackbar('Failed to copy', {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      });
    }
  };

  const handleSave = async () => {
    await saveMapping({
      data: mappingJson || {},
      module_type: section,
      webhook_url: webhookUrl,
      prod_webhook_url: prodWebhookUrl,
    });
    enqueueSnackbar(`Mapping saved successfully`, {
      variant: 'success',
      autoHideDuration: 3000,
      preventDuplicate: true,
    });
  };

  const handleReset = () => {
    setMappingJson(originalData.mappingJson);
    setWebhookUrl(originalData.webhookUrl);
    setProsWebhookUrl(originalData.prodWebhookUrl);
    enqueueSnackbar('Changes reset to original values', {
      variant: 'info',
      autoHideDuration: 3000,
      preventDuplicate: true,
    });
  };

  const handleRestoreVersion = (version: MappingVersion) => {
    setMappingJson(version.value);
    setWebhookUrl(version.webhook_url);
    setProsWebhookUrl(version.prod_webhook_url);
    setVersionDialogOpen(false);
    enqueueSnackbar(`Version ${version.version} restored. Click Save to apply changes.`, {
      variant: 'info',
      autoHideDuration: 5000,
      preventDuplicate: true,
    });
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4}>
        <Alert severity="error">Failed to load mapping. Please try again.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 3, borderRadius: 1 }}>
      {/* Left: Fields list with copy */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Available Fields
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag and drop these field names into the mapping JSON editor
        </Typography>
        <List dense>
          {fields.map((f) => (
            <ListItem
              key={f}
              divider
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/field-name', f);
                e.dataTransfer.setData('text/plain', f);
              }}
            >
              <ListItemText primary={f} />
              <ListItemSecondaryAction>
                <Tooltip title="Copy field name">
                  <IconButton edge="end" onClick={() => handleCopy(f)}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Right: JSON editor for deep mapping */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6">Field Mapping JSON</Typography>
            <Typography variant="body2" color="text.secondary">
              Map your internal fields to external field names. Labels are optional and for display only.
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<History />}
              onClick={() => setVersionDialogOpen(true)}
              disabled={versions.length === 0}
            >
              Version History
            </Button>
            {currentVersion && <Chip label={`v${currentVersion}`} size="small" color="primary" variant="outlined" />}
          </Box>
        </Box>
        <JsonEditor
          label=""
          name="mapping"
          value={mappingJson}
          onChange={setMappingJson}
          enableDndInsert
          height="400px"
          data-testid="field-mapping-json-editor"
        />

        <Box sx={{ mb: 3, mt: 4 }}>
          <TextField
            fullWidth
            label="Sandbox Webhook URL"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://your-sandbox-webhook-endpoint.com/mapping"
            // helperText="Configure a webhook URL to receive notifications when field mappings are updated."
          />
          <TextField
            fullWidth
            label="Production Webhook URL"
            value={prodWebhookUrl}
            onChange={(e) => setProsWebhookUrl(e.target.value)}
            placeholder="https://your-production-webhook-endpoint.com/mapping"
            helperText="Configure a webhook URL to receive notifications when field mappings are updated."
            sx={{ mt: 4 }}
          />
        </Box>
        {(canAdd || canUpdate) && (
          <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" disabled={!hasChanges || saving} onClick={handleReset}>
              Reset
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} /> : <Save />}
              disabled={!hasChanges || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Version History Dialog */}
      <VersionHistoryDialog
        canAddEdit={canAdd || canUpdate}
        open={versionDialogOpen}
        onClose={() => setVersionDialogOpen(false)}
        versions={versions}
        currentVersion={currentVersion}
        onRestore={handleRestoreVersion}
      />
    </Box>
  );
}
